import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import Highlighter from "react-highlight-words";
import Markdown from "react-markdown";
import ResizeDetector from "react-resize-detector";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Clear, ExpandMore, ExpandLess, ArrowDropDown, Check } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Fade,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  Menu,
  MenuItem,
  Typography as T,
  Stack,
  OutlinedInput,
  IconButton,
  Link,
  Skeleton,
} from "@mui/material";
import Chip from "catalog/components/Chip";
import EntityTag from "catalog/components/EntityTag";
import NotFoundPage from "catalog/components/NotFoundPage";
import PinButton from "catalog/components/PinButton";
import PreferredBadge from "catalog/components/PreferredBadge";
import useFilterParams, { encodeFilterParams } from "catalog/hooks/useFilterParams";
import useFormattedText from "catalog/hooks/useFormattedText";
import usePinnedEntities from "catalog/hooks/usePinnedEntities";
import { PREFERRED_ENTITIES_TAG } from "catalog/utils/buildFilterMatrix";
import queryString from "query-string";
import remarkGfm from "remark-gfm";
import { StringParam, withDefault, useQueryParams, BooleanParam } from "use-query-params";
import { TitleBreadcrumbs } from "~/components/AppHeader";
import GenerateCodeButton from "~/components/GenerateCodeButton";
import Switch from "~/components/Switch";
import useAppHeader from "~/contexts/header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { Column, TableEntity } from "~/types/interfaces/catalogResponse";

const { VITE_CATALOG_API } = import.meta.env;

interface FieldRowProps {
  field: Column;
  selectedFields: Column[];
  showPreferred: boolean;
  setSelectedFields: Dispatch<SetStateAction<Column[]>>;
  highlightText: string;
  autoExpand: boolean | null;
}

const FieldRow = ({
  field,
  selectedFields,
  showPreferred,
  setSelectedFields,
  highlightText,
  autoExpand,
}: FieldRowProps) => {
  const [descExpanded, setDescExpanded] = useState<null | boolean>(autoExpand);
  const isPreferred = field.tags.some((tag) => tag.tagFQN === PREFERRED_ENTITIES_TAG);

  return (
    <TableRow
      sx={{
        bgcolor: selectedFields
          .map(({ fullyQualifiedName }) => fullyQualifiedName)
          .includes(field.fullyQualifiedName)
          ? "primary.light"
          : "initial",
        td: {
          overflowWrap: "break-word",
          wordBreak: "normal",
        },
      }}
    >
      {/**
       * Name
       */}
      <TableCell
        sx={{
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            left: -76,
            height: 32,
            top: 0,
            width: 80,
            gap: 0.5,
            pr: 1,
          }}
        >
          <Checkbox
            size="small"
            checked={selectedFields
              .map(({ fullyQualifiedName }) => fullyQualifiedName)
              .includes(field.fullyQualifiedName)}
            onChange={(_, checked) => {
              if (checked) {
                setSelectedFields([...selectedFields, field]);
              } else {
                setSelectedFields(
                  selectedFields.filter(
                    (selectedField) => selectedField.fullyQualifiedName !== field.fullyQualifiedName
                  )
                );
              }
            }}
          />
          <PinButton fqn={field.fullyQualifiedName} showPin />
        </Box>

        <Highlighter
          highlightClassName="highlight"
          searchWords={[highlightText]}
          autoEscape={true}
          textToHighlight={field.name}
        />
      </TableCell>

      {/**
       * Datatype
       */}
      <TableCell
        sx={{
          fontFamily: "monospace",
        }}
      >
        {field.dataType}
      </TableCell>

      {showPreferred && (
        <TableCell sx={{ textAlign: "center" }}>
          {isPreferred && <Check sx={{ fontSize: 16 }} />}
        </TableCell>
      )}

      <TableCell>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {field.tags
            .filter((tag) => tag.tagFQN !== PREFERRED_ENTITIES_TAG)
            .map((tag) => (
              <Chip key={tag.tagFQN} sx={{ py: 0.5 }}>
                <EntityTag key={tag.tagFQN} tag={tag} />
              </Chip>
            ))}
        </Box>
      </TableCell>

      <TableCell sx={{ position: "relative" }}>
        {field.description && (
          <ResizeDetector handleHeight>
            {({ height, targetRef }) => {
              const showExpansionToggle =
                descExpanded !== null || Number(targetRef?.current?.scrollHeight) > Number(height);

              return (
                <Box
                  ref={targetRef}
                  sx={{
                    pr: 1,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: descExpanded ? "unset" : 3,
                  }}
                >
                  <Box
                    className="prose prose-sm max-w-none"
                    sx={{
                      "blockquote, p": {
                        my: 0,
                      },
                    }}
                  >
                    <Markdown remarkPlugins={[remarkGfm]}>{field.description}</Markdown>
                  </Box>
                  {showExpansionToggle && (
                    <IconButton
                      size="small"
                      onClick={() => setDescExpanded((prev) => !prev)}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        p: 0,
                        opacity: 0.7,
                      }}
                    >
                      {descExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )}
                </Box>
              );
            }}
          </ResizeDetector>
        )}
      </TableCell>
    </TableRow>
  );
};

const Dataset = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { setTitle } = useAppHeader();
  const { tableFQN } = useParams();
  const [params] = useFilterParams();
  const { isPinned, addEntities, removeEntities } = usePinnedEntities();
  const [columnSearch, setColumnSearch] = useQueryParams({
    field: withDefault(StringParam, ""),
    preferred: BooleanParam,
    exact: BooleanParam,
  });
  const [selectedFields, setSelectedFields] = useState<Column[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const tableQueryString = queryString.stringify({
    fields: "description,columns,tags",
  });
  const {
    data: tableData,
    isLoading,
    error,
  } = useFetcher<TableEntity>(
    `${VITE_CATALOG_API}/om/v1/tables/name/${tableFQN}?${tableQueryString}`
  );

  const { name: tableName, database, databaseSchema, tags, columns = [] } = tableData ?? {};
  const isPreferred = tags?.some((tag) => tag.tagFQN === PREFERRED_ENTITIES_TAG);
  const tableTags = tags?.filter((tag) => tag.tagFQN !== PREFERRED_ENTITIES_TAG);

  useDocumentTitle(tableName ? `${tableName} · Tables` : "Tables");

  // Set breadcrumb header when table data is loaded
  useEffect(() => {
    setTitle(
      <TitleBreadcrumbs
        crumbs={[
          { title: "Data Browser", href: "/data-browser" },
          { title: "Tables", href: "/data-browser/tables" },
          {
            title: isLoading ? <Skeleton width={75} /> : tableName,
            href: `/data-browser/tables/${tableFQN}`,
          },
        ]}
      />
    );
  }, [tableName, tableFQN, isLoading, setTitle]);

  const lineageUrl = `https://openmetadata.${window.location.hostname}/table/${tableData?.fullyQualifiedName}/lineage?fullscreen=true`;

  const highlightText = columnSearch.field || params.q || "";
  const formattedDescription = useFormattedText(tableData?.description ?? "");
  const hasPreferred = columns.some((column) =>
    column.tags.some((tag) => tag.tagFQN === PREFERRED_ENTITIES_TAG)
  );

  /**
   * Only show searched fields
   */
  const filteredFields = useMemo(
    () =>
      columns
        .filter((column) => {
          if (columnSearch.exact) {
            return column.name.toLowerCase() === columnSearch.field.toLowerCase();
          }
          return (
            column.name?.toLowerCase().includes(columnSearch.field.toLowerCase()) ||
            column.description?.toLowerCase().includes(columnSearch.field.toLowerCase())
          );
        })
        .filter((column) => {
          if (columnSearch.preferred) {
            return column.tags.some((tag) => tag.tagFQN === PREFERRED_ENTITIES_TAG);
          }
          return true;
        }),
    [columnSearch, columns]
  );

  if (error || (!isLoading && !tableData)) {
    return <NotFoundPage />;
  }

  return (
    <Fade in>
      <Card
        sx={{
          px: 12,
          py: 2,
          boxShadow: "none",
        }}
      >
        <Stack pb={6} gap={2}>
          <Stack>
            {/**
             * Back
             */}
            <Box width="fit-content">
              <Button
                sx={{
                  color: "inherit",
                  mb: 1,
                }}
                startIcon={
                  <svg height="12" viewBox="0 0 6 11" fill="none">
                    <path
                      d="M5 10.7764L0 5.77637L5 0.776367L5.8875 1.66387L1.775 5.77637L5.8875 9.88887L5 10.7764Z"
                      fill="black"
                    />
                  </svg>
                }
                onClick={() => {
                  const qs = queryString.stringify(encodeFilterParams(params));
                  if (state?.referrer) {
                    navigate(`../${state.referrer}?${qs}`);
                  } else {
                    // Handle router link state being lost when dataset is opened in a new tab or browser is refreshed
                    navigate(`../tables?${qs}`);
                  }
                }}
              >
                Back
              </Button>
            </Box>
            <Box
              sx={{
                fontWeight: 500,
                fontSize: 24,
                display: "flex",
                alignItems: "center",
                gap: 1,
                position: "relative",
              }}
            >
              <Box sx={{ position: "absolute", top: -2, left: -25 }}>
                <PinButton fqn={tableData?.fullyQualifiedName ?? ""} showPin />
              </Box>
              {!isLoading ? tableName : <Skeleton width={200} />}
              {isPreferred && <PreferredBadge />}
            </Box>
            <Box fontSize={14} color="grey.600">
              {!isLoading ? (
                [database?.name, databaseSchema?.name].join(".")
              ) : (
                <Skeleton width={250} />
              )}
            </Box>
          </Stack>

          {/**
           * Descriptions
           */}
          {Boolean(formattedDescription) && (
            <T fontSize={14} color="primary.dark">
              {formattedDescription}
            </T>
          )}

          {(isLoading || !!tableTags?.length) && (
            <Box>
              <T variant="label" fontSize={12}>
                Tags
              </T>
              {!isLoading ? (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {tableTags?.map((tag) => (
                    <Chip key={tag.tagFQN} sx={{ py: 0.5 }}>
                      <EntityTag key={tag.tagFQN} tag={tag} />
                    </Chip>
                  ))}
                </Box>
              ) : (
                <Skeleton width={100} sx={{ mt: -0.5 }} />
              )}
            </Box>
          )}

          <Box display="flex" gap={1} alignItems="center" my={1}>
            <Link target="_blank" href={lineageUrl}>
              <Button variant="outlined">View Lineage</Button>
            </Link>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <OutlinedInput
              autoFocus
              size="small"
              placeholder="Search columns..."
              value={columnSearch.field ?? ""}
              endAdornment={
                <IconButton
                  edge="end"
                  size="small"
                  aria-label="Clear search"
                  sx={{ visibility: columnSearch.field ? "visible" : "hidden" }}
                  onClick={() =>
                    setColumnSearch({ field: undefined, exact: undefined }, "replaceIn")
                  }
                >
                  <Clear fontSize="small" />
                </IconButton>
              }
              onChange={(event) => {
                const value = event.target.value;
                setColumnSearch(
                  {
                    field: value ? value : undefined,
                    exact: undefined,
                  },
                  "replaceIn"
                );
              }}
              sx={{
                height: 36,
                width: 240,
                fontSize: 14,
                pr: 1,
              }}
            />

            <Button
              endIcon={<ArrowDropDown />}
              variant="outlined"
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              Select
            </Button>

            {/**
             * Select menu
             */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              TransitionProps={{
                timeout: 0,
              }}
              sx={{
                mt: 0.5,
              }}
              PaperProps={{
                sx: {
                  pb: 1,
                },
                elevation: 1,
              }}
            >
              <MenuItem
                onClick={() => {
                  setSelectedFields(filteredFields);
                  setAnchorEl(null);
                }}
              >
                All
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSelectedFields([]);
                  setAnchorEl(null);
                }}
              >
                None
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSelectedFields(
                    filteredFields.filter((field) => isPinned(field.fullyQualifiedName))
                  );
                  setAnchorEl(null);
                }}
              >
                Pinned
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSelectedFields(
                    filteredFields.filter((field) => !isPinned(field.fullyQualifiedName))
                  );
                  setAnchorEl(null);
                }}
              >
                Unpinned
              </MenuItem>
            </Menu>

            {/**
             * Pin
             */}
            {selectedFields.length > 0 && (
              <Button
                variant="outlined"
                startIcon={
                  <svg width="9" height="14" viewBox="0 0 9 14" fill="none">
                    <path
                      d="M6.71191 6.35L8.01191 7.65V8.95H4.76191V12.85L4.11191 13.5L3.46191 12.85V8.95H0.211914L0.211914 7.65L1.51191 6.35L1.51191 1.8H0.861914V0.5L7.36191 0.5V1.8H6.71191V6.35Z"
                      fill="currentColor"
                    />
                  </svg>
                }
                onClick={() =>
                  addEntities(selectedFields.map(({ fullyQualifiedName }) => fullyQualifiedName))
                }
              >
                Pin
              </Button>
            )}

            {/**
             * Unpin
             */}
            {selectedFields.length > 0 && (
              <Button
                variant="outlined"
                startIcon={
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <g clipPath="url(#clip0_53_2958)">
                      <path
                        d="M6 5.50016L10.6667 10.1668H8.66667V12.8335L8 14.8335L7.33333 12.8335V10.1668H4V8.16683L6 6.16683V5.50016ZM11.3333 0.833496V2.16683L10 2.8335V6.16683L12 8.16683V9.8075L8.18 5.98683L6 3.80683V2.8335L4.66667 2.16683V0.833496H11.3333Z"
                        fill="currentColor"
                      />
                      <path
                        d="M1.51341 1.01318L0.666748 1.85985L13.6401 14.8332L14.4867 13.9865L7.33341 6.83318L1.51341 1.01318Z"
                        fill="currentColor"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_53_2958">
                        <rect width="15" height="15" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                }
                onClick={() =>
                  removeEntities(selectedFields.map(({ fullyQualifiedName }) => fullyQualifiedName))
                }
              >
                Unpin
              </Button>
            )}
            <GenerateCodeButton
              selections={[
                {
                  schema: databaseSchema?.name,
                  table: tableName,
                  variables: selectedFields.map(({ name }) => name),
                },
              ]}
            />
            {hasPreferred && (
              <Box display="flex" gap={1} alignItems="center">
                <Switch
                  checked={!!columnSearch.preferred}
                  onClick={() =>
                    setColumnSearch(
                      {
                        preferred: !columnSearch.preferred || undefined,
                        field: columnSearch.field,
                        exact: undefined,
                      },
                      "replaceIn"
                    )
                  }
                />
                <T variant="body2" whiteSpace="nowrap">
                  Preferred only
                </T>
              </Box>
            )}
          </Box>

          {/**
           * Schema
           */}
          {columns?.length > 0 && (
            <Table
              size="small"
              sx={{
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: "grey.300",
                borderRadius: 1,
                borderCollapse: "initial",
                "& td": { verticalAlign: "top" },
                "& td:not(:last-of-type), & th:not(:last-of-type)": {
                  borderRightWidth: 1,
                  borderRightStyle: "solid",
                  borderRightColor: "grey.300",
                },
                "& tr:last-child td": {
                  borderBottom: "none",
                },
              }}
            >
              <TableHead
                sx={{
                  bgcolor: "grey.100",
                  "& th": {
                    fontSize: 12,
                    color: "common.black",
                    fontWeight: 600,
                    textWrap: "nowrap",
                  },
                }}
              >
                <TableRow>
                  <TableCell>NAME</TableCell>
                  <TableCell>TYPE</TableCell>
                  {hasPreferred && <TableCell sx={{ width: 0 }}>PREFERRED</TableCell>}
                  <TableCell sx={{ flex: 1, minWidth: 300 }}>TAGS</TableCell>
                  <TableCell sx={{ flex: 1, minWidth: 300 }}>DESCRIPTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFields.map((field) => (
                  <FieldRow
                    key={field.fullyQualifiedName}
                    field={field}
                    showPreferred={hasPreferred}
                    selectedFields={selectedFields}
                    setSelectedFields={setSelectedFields}
                    highlightText={highlightText}
                    autoExpand={columnSearch.field === field.name ? true : null}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      </Card>
    </Fade>
  );
};

export default Dataset;
